import { CanView } from 'components/Can'
import { CleanupRightRail } from 'components/CleanupMode/CleanupRightRail'
import { DecisionModeRightRail } from 'components/DecisionMode/DecisionModeRightRail'
import { COMMENT_VIEW, RIGHT_RAIL_PARAM, TASKS_VIEW } from 'components/DocRightRail/Constants'
import { DocRightRailV2 } from 'components/DocRightRailV2/DocRightRailV2'
import { FloatingDocRightRail } from 'components/DocRightRailV2/FloatingDocRightRail/FloatingDocRightRail'
import { AnimateSlideInOut } from 'components/Sidebar/AnimateSlideInOut'
import { useQueryParams } from 'hooks/useQueryParams'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { observer } from 'mobx-react-lite'
import { useProseInstance } from 'providers/ProseInstanceProvider'
import { useCallback, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useDocStore } from 'stores/DocStore'
import { DocMode } from 'stores/DocStore.types'
import { ArticleFlat, ArticleView } from 'types'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'

const EditorRightRail = observer(({ article }: { article: ArticleFlat }) => {
  const isSystemRedesignEnabled = useIsSystemRedesign()
  const { isMobile } = useResponsiveDesign()
  const { currentViewRef } = useProseInstance()
  const docStore = useDocStore()
  const { right_rail: rightRailQueryParam, id } = useQueryParams()
  const collabItemIdQueryParam = parseInt(id)
  const history = useHistory()
  const { search, pathname, state, hash } = useLocation()

  const removeDeeplinkParams = useCallback(() => {
    // Remove deeplink params
    const urlSearchParams = new URLSearchParams(search)
    urlSearchParams.delete(RIGHT_RAIL_PARAM)
    urlSearchParams.delete('id')
    history.replace({ pathname, state, hash, search: urlSearchParams.toString() })
  }, [history, search, hash, pathname, state])

  /* -------------------------------------------------------------------------- */
  /*                          Collab item link handlers                         */
  /* -------------------------------------------------------------------------- */

  const linkedComment = docStore.comments.find((comment) => comment.id === collabItemIdQueryParam)
  useEffect(() => {
    if (rightRailQueryParam === COMMENT_VIEW && collabItemIdQueryParam) {
      removeDeeplinkParams()
      if (linkedComment) {
        docStore.rootStore.comments.select(collabItemIdQueryParam)
        docStore.rightRailStore.setActiveTab(COMMENT_VIEW)
        if (linkedComment.status === 'resolved') {
          docStore.filters.activate('resolved')
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedComment?.id, collabItemIdQueryParam, rightRailQueryParam, removeDeeplinkParams])

  const linkedTodo = docStore.todos.find((todo) => todo.id === collabItemIdQueryParam)
  useEffect(() => {
    if (rightRailQueryParam === TASKS_VIEW && collabItemIdQueryParam) {
      removeDeeplinkParams()
      if (linkedTodo) {
        docStore.rootStore.todos.select(collabItemIdQueryParam)
        docStore.rightRailStore.setActiveTab(COMMENT_VIEW)
        if (Boolean(linkedTodo.completed_at)) {
          docStore.filters.activate('resolved')
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedTodo?.id, collabItemIdQueryParam, rightRailQueryParam, removeDeeplinkParams])

  if (isMobile) {
    return null
  }

  return (
    <>
      {!isSystemRedesignEnabled && (
        <CanView inViewMode={ArticleView.Editing} inWorkspace={article.company_id}>
          <FloatingDocRightRail article={article} />
        </CanView>
      )}
      <AnimateSlideInOut in={!!docStore.rightRailStore.locked}>
        <DocRightRailV2 view={currentViewRef.current} article={article} />
      </AnimateSlideInOut>
    </>
  )
})

const ModeRightRail = {
  [DocMode.Cleanup]: CleanupRightRail,
  [DocMode.Decision]: DecisionModeRightRail,
  [DocMode.Editor]: EditorRightRail,
  [DocMode.Focus]: EditorRightRail,
}

export const RightRail = observer(({ article }: { article: ArticleFlat }) => {
  const docStore = useDocStore()

  if (!docStore) return null

  const RightRailToRender = ModeRightRail[docStore.docMode] || EditorRightRail

  return <RightRailToRender article={article} />
})
